import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import '../../styles/AdminEditUser.css';

function AdminEditUser() {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [targetUser, setTargetUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    status: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if not admin
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  // Fetch user details
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.post(
          'https://n8n.iniai.cloud/webhook/get-user',
          { user_id: userId },
          {
            withCredentials: true,
            headers: {
              'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY
            }
          }
        );

        if (response.data[0]?.success === 'true' || response.data[0]?.success === true) {
          const userData = response.data[0].user;
          setTargetUser(userData);
          setFormData({
            name: userData.name || '',
            email: userData.email || '',
            role: userData.role || '',
            status: userData.status || 'active',
            password: '',
            confirmPassword: ''
          });
        } else {
          setError('User not found');
        }
      } catch (err) {
        setError('Error fetching user: ' + (err.response?.data?.message || err.message));
      } finally {
        setIsLoadingUser(false);
      }
    };

    if (userId && user?.role === 'admin') {
      fetchUser();
    }
  }, [userId, user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear messages when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }
    
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }

    if (!formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }

    if (!formData.role.trim()) {
      setError('Role is required');
      return false;
    }

    if (formData.password || formData.confirmPassword) {
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters long');
        return false;
      }
      
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const updateData = {
        user_id: userId,
        name: formData.name.trim(),
        email: formData.email.trim(),
        role: formData.role.trim(),
        status: formData.status.trim(),
        admin_update: true // Flag to indicate this is an admin update
      };

      // Only include password if it's being changed
      if (formData.password) {
        updateData.password = formData.password;
      }

      const response = await axios.post(
        'https://n8n.iniai.cloud/webhook/admin-update-user',
        updateData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY
          }
        }
      );
      
      if (response.data[0]?.success === 'true' || response.data[0]?.success === true) {
        setSuccess('User updated successfully!');
        // Update local user data
        setTargetUser(response.data[0].user);
        // Clear password fields after successful update
        setFormData(prev => ({
          ...prev,
          password: '',
          confirmPassword: ''
        }));
      } else {
        setError(response.data[0]?.message || 'Update failed');
      }
    } catch (err) {
      setError('Update failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (user?.role !== 'admin') {
    return <div className="admin-edit-user-container">Access denied. Admin role required.</div>;
  }

  if (isLoadingUser) {
    return <div className="admin-edit-user-container">Loading user details...</div>;
  }

  if (!targetUser) {
    return <div className="admin-edit-user-container">User not found.</div>;
  }

  return (
    <div className="admin-edit-user-container">
      <div className="admin-edit-user-header">
        <button 
          className="back-button"
          onClick={() => navigate('/admin/users')}
        >
          ← Back to Users
        </button>
        <h2>Edit User: {targetUser.name}</h2>
      </div>

      <form onSubmit={handleSubmit} className="admin-edit-user-form">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="form-group">
          <label htmlFor="name">Name:</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="role">Role:</label>
          <select
            id="role"
            name="role"
            value={formData.role}
            onChange={handleInputChange}
            required
            disabled={isLoading}
          >
            <option value="">Select a role</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
            <option value="manager">Manager</option>
            <option value="developer">Developer</option>
            <option value="volvo">Volvo</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="status">Status:</label>
          <select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            required
            disabled={isLoading}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>

        <div className="password-section">
          <h3>Change Password (Optional)</h3>
          <p className="password-help">Leave blank to keep current password</p>
          
          <div className="form-group">
            <label htmlFor="password">New Password:</label>
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                disabled={isLoading}
                placeholder="Enter new password (min 6 characters)"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
                disabled={isLoading}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password:</label>
            <input
              type={showPassword ? "text" : "password"}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              disabled={isLoading}
              placeholder="Confirm new password"
            />
          </div>
        </div>

        <div className="button-group">
          <button 
            type="button"
            onClick={() => navigate('/admin/users')}
            className="cancel-button"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            disabled={isLoading}
            className="update-button"
          >
            {isLoading ? 'Updating...' : 'Update User'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminEditUser;